import {SpotInspection, SpotInspectionMeasurement} from '@domain/shifthandover/src/events/types'
import {Box, Typography} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'

type Props = {
  spotInspections: SpotInspection[]
}

const groupMeasurementsByRegion = (
  spotInspections: SpotInspection[]
): [string, SpotInspectionMeasurement[]][] => {
  const regionMap = new Map<string, SpotInspectionMeasurement[]>()
  for (const inspection of spotInspections) {
    for (const measurement of inspection.spotInspectionMeasurements ?? []) {
      if (measurement.threshold === undefined) continue
      const existing = regionMap.get(measurement.region) ?? []
      existing.push(measurement)
      regionMap.set(measurement.region, existing)
    }
  }
  return Array.from(regionMap.entries())
}

export const SpotInspectionDetails: React.FC<Props> = ({spotInspections}) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)

  const regionEntries = useMemo(() => groupMeasurementsByRegion(spotInspections), [spotInspections])

  if (!regionEntries.length) {
    return null
  }

  return (
    <>
      <Typography variant="h4" mb={2}>
        {t('shiftEvent.label.inspectionAlertInformation')}
      </Typography>
      {regionEntries.map(([region, measurements], regionIndex) => (
        <Box
          key={region}
          data-test-id={`spot-region-${region}`}
          mb={regionIndex === regionEntries.length - 1 ? 0 : 2}
        >
          <Typography variant="body2" fontWeight="bold" mb={0.5}>
            {region}
          </Typography>
          {measurements.map((measurement, index) => (
            <Box
              key={measurement.id}
              data-test-id={`spot-measurement-${measurement.id}`}
              mb={index === measurements.length - 1 ? 0 : 1}
            >
              {measurement.threshold !== undefined && (
                <Typography variant="body2" color="text.secondary">
                  {t('shiftEvent.label.threshold')}: {measurement.threshold} {measurement.unit}
                </Typography>
              )}
              {measurement.value !== undefined && (
                <Typography variant="body2">
                  {measurement.name}: {measurement.value} {measurement.unit}
                </Typography>
              )}
            </Box>
          ))}
        </Box>
      ))}
    </>
  )
}
